import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import styles from "./DealerAnalytics.module.scss";
import { getDealerDigestMarkdown } from "../../../services/dealerAnalytics";
import { Segmented, clearReportCache, fmtRange } from "./shared";
import Overview from "./Overview";
import Demand from "./Demand";
import Searches from "./Searches";
import Listings from "./Listings";
import Sources from "./Sources";
import Customers from "./Customers";
import Sales from "./Sales";

const PERIODS = [
  { value: 7, label: "7 days" },
  { value: 14, label: "14 days" },
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 365, label: "12 months" },
  { value: "custom", label: "Custom" },
];

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "demand", label: "Popular models" },
  { id: "searches", label: "Searches" },
  { id: "listings", label: "Listings" },
  { id: "sources", label: "Sources & SEO" },
  { id: "customers", label: "Customers" },
  { id: "sales", label: "Sales" },
];

// The API's limits on a custom range (dealerAnalytics.controller windowFromQuery).
const EARLIEST_DAY = "2025-01-01";
const MAX_RANGE_DAYS = 366;

// Calendar days in US Eastern, where the reports count them.
const todayKey = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });
const addDays = (key, n) => {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};
const daysBetween = (fromKey, toKey) => Math.round((Date.parse(toKey) - Date.parse(fromKey)) / 86400000) + 1;

// The days a period covers and the ones it is compared with, worked out here
// the same way the API does, so the line under the controls is right before
// any report has loaded.
const describe = (period) => {
  const to = period.custom ? period.to : todayKey();
  const from = period.custom ? period.from : addDays(to, -(period.days - 1));
  const days = daysBetween(from, to);
  return { from, to, days, previousFrom: addDays(from, -days), previousTo: addDays(from, -1) };
};

const rangeProblem = (from, to) => {
  if (!from || !to) return "Pick both dates.";
  if (from > to) return "The start date is after the end date.";
  if (from > todayKey()) return "The start date is in the future.";
  if (from < EARLIEST_DAY) return "Records start in 2025.";
  if (daysBetween(from, to > todayKey() ? todayKey() : to) > MAX_RANGE_DAYS) return "Pick a range of a year or less.";
  return "";
};

/**
 * Dealer Stock Analytics: what people look at, search for, save, ask about and
 * buy on both sites' dealer stock, where they come from, and what is waiting on
 * the team. Every tab is one report from /analytics/dealer/* for the chosen
 * period: the last 7 to 365 days, or a custom range of days. "Copy for Claude"
 * fetches the same numbers as one markdown digest, without personal data, to
 * paste into a Claude conversation.
 *
 * Opening a lead or a customer hands over to the Dealer Stock tab through the
 * URL (?lead= / ?customer= / ?customerLead=), where those panels live.
 */
const DealerAnalytics = () => {
  // { days } for the last N days, or { custom, from, to } (YYYY-MM-DD, inclusive).
  const [period, setPeriod] = useState({ days: 30 });
  const [customOpen, setCustomOpen] = useState(false);
  const [draft, setDraft] = useState({ from: "", to: "" });
  const [tab, setTab] = useState("overview");
  const [refreshKey, setRefreshKey] = useState(0);
  const [digestState, setDigestState] = useState("");
  const [, setSearchParams] = useSearchParams();

  const range = period.custom ? `from=${period.from}&to=${period.to}` : `days=${period.days}`;
  const shown = describe(period);
  const draftProblem = rangeProblem(draft.from, draft.to);

  const choosePeriod = (value) => {
    setDigestState("");
    if (value === "custom") {
      // Start the pickers on whatever is showing now.
      setDraft({ from: shown.from, to: shown.to });
      setCustomOpen(true);
      return;
    }
    setCustomOpen(false);
    setPeriod({ days: value });
  };

  const applyCustom = (e) => {
    e.preventDefault();
    if (draftProblem) return;
    const to = draft.to > todayKey() ? todayKey() : draft.to;
    setPeriod({ custom: true, from: draft.from, to });
    setDigestState("");
  };

  const refresh = () => {
    clearReportCache();
    setRefreshKey((k) => k + 1);
  };

  const loadDigest = async () => {
    setDigestState("loading");
    try {
      const md = await getDealerDigestMarkdown(range);
      if (typeof md !== "string" || !md.startsWith("#")) throw new Error("No digest");
      return md;
    } catch (err) {
      setDigestState("failed");
      return null;
    }
  };

  const download = (md) => {
    const url = URL.createObjectURL(new Blob([md], { type: "text/markdown" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = period.custom
      ? `dealer-analytics-${period.from}-to-${period.to}.md`
      : `dealer-analytics-${period.days}d-${todayKey()}.md`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    setDigestState("saved");
  };

  const copyDigest = async () => {
    const md = await loadDigest();
    if (!md) return;
    try {
      await navigator.clipboard.writeText(md);
      setDigestState("copied");
    } catch (err) {
      // No clipboard (an http page, or permission refused): save it instead.
      download(md);
    }
  };

  const saveDigest = async () => {
    const md = await loadDigest();
    if (md) download(md);
  };

  const openLead = (inquiryId) => setSearchParams({ lead: String(inquiryId) });
  const openCustomer = (ref) => {
    if (ref.userId) setSearchParams({ customer: String(ref.userId) });
    else if (ref.inquiryId) setSearchParams({ customerLead: String(ref.inquiryId) });
  };

  const digestLabel = {
    loading: "Preparing…",
    copied: "Copied: paste into Claude",
    saved: "Saved as a file",
    failed: "Could not build it",
  }[digestState] || "Copy for Claude";

  const props = { range, refreshKey };
  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div>
          <h1>Dealer Stock Analytics</h1>
          <p>www.importjdmmagic.com and the portal&apos;s dealer stock together. Days are US Eastern.</p>
        </div>
        <div className={styles.controls}>
          <Segmented
            label="Period"
            options={PERIODS}
            value={customOpen || period.custom ? "custom" : period.days}
            onChange={choosePeriod}
          />
          <button type="button" className={styles.actionBtn} onClick={refresh}>Refresh</button>
          <button
            type="button"
            className={styles.primaryBtn}
            onClick={copyDigest}
            disabled={digestState === "loading"}
            title="Every report for this period as one markdown summary, with no names, emails or phone numbers, to paste into Claude"
          >
            {digestLabel}
          </button>
          <button type="button" className={styles.actionBtn} onClick={saveDigest} disabled={digestState === "loading"}>Download .md</button>
        </div>
      </div>

      {customOpen && (
        <form className={styles.rangeForm} onSubmit={applyCustom}>
          <label className={styles.rangeField}>
            From
            <input
              type="date"
              className={styles.dateInput}
              min={EARLIEST_DAY}
              max={todayKey()}
              value={draft.from}
              onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
            />
          </label>
          <label className={styles.rangeField}>
            To
            <input
              type="date"
              className={styles.dateInput}
              min={draft.from || EARLIEST_DAY}
              max={todayKey()}
              value={draft.to}
              onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
            />
          </label>
          <button type="submit" className={styles.primaryBtn} disabled={!!draftProblem}>Apply</button>
          {draftProblem
            ? <span className={styles.rangeHint} role="alert">{draftProblem}</span>
            : <span className={styles.rangeHint}>{daysBetween(draft.from, draft.to)} days, both dates included</span>}
        </form>
      )}

      <p className={styles.periodLine}>
        Showing <strong>{fmtRange(shown.from, shown.to)}</strong> ({shown.days} {shown.days === 1 ? "day" : "days"}),
        compared with {fmtRange(shown.previousFrom, shown.previousTo)}.
      </p>

      <div className={styles.tabs} role="tablist">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={tab === t.id}
            className={`${styles.tab} ${tab === t.id ? styles.tabActive : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "overview" && <Overview {...props} onOpenLead={openLead} />}
      {tab === "demand" && <Demand {...props} />}
      {tab === "searches" && <Searches {...props} />}
      {tab === "listings" && <Listings {...props} />}
      {tab === "sources" && <Sources {...props} />}
      {tab === "customers" && <Customers {...props} onOpenCustomer={openCustomer} />}
      {tab === "sales" && <Sales {...props} />}
    </div>
  );
};

export default DealerAnalytics;
